// Operator-scoped passenger-bus service.
//
// The bus-operator-portal views import from HERE instead of '@services/passenger-bus'.
// These functions hit the operator/* endpoints, which the API scopes to the
// caller's own operatorId (from the token) and forbids agency actions
// (review/approve/reject/decision). Reference-data helpers are re-exported
// unchanged from the shared service.
//
// Agency views keep importing the shared service ('@services/passenger-bus').
import api from '@services'
import { stripEmptyObjects } from '@utils'

const serviceBaseUrl = process.env.REACT_APP_PASSENGER_BUS_BASE_URL || ''
const operatorPrefix = `${serviceBaseUrl}/svc/passenger-bus/operator`
const basePrefix = `${serviceBaseUrl}/svc/passenger-bus`

// ---- Route applications (own) ----
export const getRouteApplications = async (params) => api.get(`${operatorPrefix}/route-applications`, { params: stripEmptyObjects(params) }).then((r) => r.data)
export const getRouteApplication = async (id) => api.get(`${operatorPrefix}/route-applications/${id}`).then((r) => r.data)
export const submitRouteApplication = async (payload) => {
  const isFormData = payload instanceof FormData
  const response = await api.post(
    `${operatorPrefix}/route-applications`,
    payload,
    isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}
  )
  return response.data
}
export const updateRouteApplication = async (payload) => {
  const { id, ...body } = payload
  return api.patch(`${operatorPrefix}/route-applications/${id}`, body).then((r) => r.data)
}
export const submitRouteApplicationForReview = async (id) => api.put(`${operatorPrefix}/route-applications/${id}/submit`).then((r) => r.data)
export const checkScheduleAvailability = async (slots) => api.post(`${basePrefix}/schedule-availability/check`, { slots }).then((r) => r.data)
export const proposePlace = async (payload) => api.post(`${operatorPrefix}/places`, payload).then((r) => r.data)

// ** Operator notifications (dashboard bell)
export const getOperatorNotifications = async (unread = false) => api.get(`${operatorPrefix}/notifications`, { params: unread ? { unread: 'true' } : {} }).then((r) => r.data)
export const getOperatorNotificationUnreadCount = async () => api.get(`${operatorPrefix}/notifications/unread-count`).then((r) => r.data)
export const markOperatorNotificationRead = async (id) => api.patch(`${operatorPrefix}/notifications/${id}/read`).then((r) => r.data)

// ---- Route permits (own) ----
export const getRoutePermits = async (params) => api.get(`${operatorPrefix}/route-permits`, { params: stripEmptyObjects(params) }).then((r) => r.data)
export const getRoutePermit = async (id) => api.get(`${operatorPrefix}/route-permits/${id}`).then((r) => r.data)
export const addRoutePermit = async (payload) => api.post(`${operatorPrefix}/route-permits`, payload).then((r) => r.data)
export const submitRoutePermit = async (id) => api.put(`${operatorPrefix}/route-permits/${id}/submit`).then((r) => r.data)

// ---- Route suspensions (own) ----
export const getRouteSuspensions = async (params) => api.get(`${operatorPrefix}/route-suspensions`, { params: stripEmptyObjects(params) }).then((r) => r.data)
export const getRouteSuspension = async (id) => api.get(`${operatorPrefix}/route-suspensions/${id}`).then((r) => r.data)
export const addRouteSuspension = async (payload) => api.post(`${operatorPrefix}/route-suspensions`, payload).then((r) => r.data)
export const submitRouteSuspension = async (id) => api.put(`${operatorPrefix}/route-suspensions/${id}/submit`).then((r) => r.data)

// ---- Route cancellations (own) ----
export const getRouteCancellations = async (params) => api.get(`${operatorPrefix}/route-cancellations`, { params: stripEmptyObjects(params) }).then((r) => r.data)
export const getRouteCancellation = async (id) => api.get(`${operatorPrefix}/route-cancellations/${id}`).then((r) => r.data)
export const addRouteCancellation = async (payload) => api.post(`${operatorPrefix}/route-cancellations`, payload).then((r) => r.data)
export const submitRouteCancellation = async (id) => api.put(`${operatorPrefix}/route-cancellations/${id}/submit`).then((r) => r.data)
// server derives the operator from the token; the id arg is ignored
export const checkOperatorBarred = async () => api.get(`${operatorPrefix}/route-cancellations/check-barred`).then((r) => r.data)

// ---- Bus inspection applications (own) ----
export const getBusInspectionApplications = async (params) => api.get(`${operatorPrefix}/bus-inspection-applications`, { params: stripEmptyObjects(params) }).then((r) => r.data)
export const getBusInspectionApplication = async (id) => api.get(`${operatorPrefix}/bus-inspection-applications/${id}`).then((r) => r.data)
export const addBusInspectionApplication = async (payload) => api.post(`${operatorPrefix}/bus-inspection-applications`, payload).then((r) => r.data)
// own application's inspection report (ownership-checked server-side)
export const getInspectionReportByApplication = async (applicationId) => api.get(`${operatorPrefix}/bus-inspection-applications/${applicationId}/report`).then((r) => r.data)

// ---- Document URLs / downloads ----
// Stored *Url fields hold an S3 key; build a viewable URL with the S3 base.
export { openS3Doc } from './docUrl'
export { apiErrorMessage } from './apiError'

// Decision-letter PDF for my own application (server streams the PDF).
export const downloadOperatorDecisionLetter = async (id) => {
  const response = await api.get(
    `${operatorPrefix}/route-applications/${id}/download-decision-letter`,
    { responseType: 'blob' }
  )
  return response.data
}

// ---- Document upload ----
// Uploads a File to S3 via the operator endpoint; returns { url, filename }
// where url is the S3 key to store in the create payload's *Url field.
export const uploadOperatorDocument = async (file) => {
  const form = new FormData()
  form.append('file', file)
  const response = await api.post(`${operatorPrefix}/documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response.data
}

// ---- Operator self-profile ----
// server returns the caller's own record; the id arg is ignored.
export const getOperator = async () => api.get(`${basePrefix}/operators/me`).then((r) => r.data)

// ---- Reference data + own inspection report: unchanged, re-exported ----
export {
  getBusTypes,
  getRegionalOffices,
  getDzongkhag,
  getDzongkhags,
  getPlace,
  getPlaces,
  getRoute,
  getRoutes,
  getDriverCertificationsByCid,
  getDriverCertificationByLicenseNo,
  getConductorCertificationsByCid
} from './index'
